/**
 * When a chef printout's counts lock (PROD-1452), shared by the print dialog
 * (apps/ordo client) and the "orders ready" send (apps/ordo server, PROD-1503).
 *
 * Schools change quantities until their own order deadline, so a packet
 * printed before it is out of date and a chef who cooks from it shorts the
 * school. A printed day is still open while ANY school on it can change its
 * order:
 *   - every shipment on the day, against its school's cutoff for the
 *     shipment's SERVICE date (the day the school orders for), and
 *   - every school the kitchen serves that has no order on the day yet (it
 *     can still place one), unless the day is a no-service day for it.
 * The printout locks when the LAST of those windows closes. Each window also
 * names its day and school (`openPrintWindows`), so a printout can mark only
 * the pages whose counts can still change.
 *
 * Inputs are structural so the browser's API types and the server's Prisma
 * rows both fit without a mapping layer. Pure TS, like the rest of this
 * package.
 */

import { type DayOfWeek, orderingCutoff } from './order-deadline'

/** A school's order-deadline policy as the chef surfaces carry it. */
export interface PrintLockDeadline {
  /**
   * `FIXED_WEEKLY` or `RELATIVE_DAYS`. Null, absent (a payload from a server
   * that predates the field) or anything else reads as FIXED_WEEKLY.
   */
  orderDeadlineMode?: string | null
  orderDeadlineDayOfWeek: string | null
  orderDeadlineWeeksBefore: number
  orderDeadlineTime: string
  orderDeadlineRelativeDays?: number | null
  timeZone: string
}

export interface PrintLockShipment {
  schoolLocationId: string
  /** `@db.Date` instant or a `YYYY-MM-DD…` string. */
  serviceDate: Date | string
  deliveryDate: Date | string
  destinationSchoolDeadline: PrintLockDeadline
}

export interface PrintLockServedSchool {
  schoolLocationId: string
  deadline: PrintLockDeadline
}

export interface PrintLockNoServiceDay {
  schoolLocationId: string
  /** `YYYY-MM-DD` (a longer ISO string is sliced). */
  date: string
}

export interface OrdersFinalInput {
  /** Printed days (`YYYY-MM-DD`, DELIVERY axis: the chef surfaces' day). */
  dayKeys: Iterable<string>
  shipments: ReadonlyArray<PrintLockShipment>
  servedSchools: ReadonlyArray<PrintLockServedSchool>
  noServiceDays?: ReadonlyArray<PrintLockNoServiceDay>
}

export interface PrintLockInput extends OrdersFinalInput {
  now: Date
}

/** UTC day key; a string passes through. Mirrors `shipmentDayKey` in `@ordo/constants`. */
function dayKeyOf(value: Date | string): string {
  return typeof value === 'string' ? value.slice(0, 10) : value.toISOString().slice(0, 10)
}

/** UTC noon of a `YYYY-MM-DD` key, so no zone offset can roll it onto another day. */
function dateFromDayKey(key: string): Date {
  const [y, m, d] = key.split('-').map(Number)
  return new Date(Date.UTC(y ?? 1970, (m ?? 1) - 1, d ?? 1, 12))
}

/** The instant ordering for `serviceDayKey` closes under a school's policy (service-day midnight when none is set). */
function orderingCutoffFor(serviceDayKey: string, deadline: PrintLockDeadline): Date {
  return orderingCutoff(dateFromDayKey(serviceDayKey), {
    orderDeadlineMode: deadline.orderDeadlineMode === 'RELATIVE_DAYS' ? 'RELATIVE_DAYS' : 'FIXED_WEEKLY',
    orderDeadlineDayOfWeek: deadline.orderDeadlineDayOfWeek as DayOfWeek | null,
    orderDeadlineWeeksBefore: deadline.orderDeadlineWeeksBefore,
    orderDeadlineTime: deadline.orderDeadlineTime,
    relativeDaysBeforeService: deadline.orderDeadlineRelativeDays ?? null,
    timeZone: deadline.timeZone,
  })
}

/** One ordering window on a printed day: the school whose order it is and when it closes. */
export interface PrintWindow {
  /** The printed day (`YYYY-MM-DD`, DELIVERY axis). */
  dayKey: string
  schoolLocationId: string
  closesAt: Date
}

/** Every ordering window that can still change the printed days, past or future. */
function printWindows({ dayKeys, shipments, servedSchools, noServiceDays = [] }: OrdersFinalInput): PrintWindow[] {
  const days = new Set(dayKeys)
  const windows: PrintWindow[] = []
  const ordered = new Set<string>()
  for (const shipment of shipments) {
    const dayKey = dayKeyOf(shipment.deliveryDate)
    if (!days.has(dayKey)) {
      continue
    }
    ordered.add(`${dayKey}|${shipment.schoolLocationId}`)
    windows.push({
      dayKey,
      schoolLocationId: shipment.schoolLocationId,
      closesAt: orderingCutoffFor(dayKeyOf(shipment.serviceDate), shipment.destinationSchoolDeadline),
    })
  }
  const noService = new Set(noServiceDays.map((d) => `${d.date.slice(0, 10)}|${d.schoolLocationId}`))
  for (const dayKey of days) {
    for (const school of servedSchools) {
      // ponytail: a school that has not ordered yet is judged as if the printed
      // day were its service day. A day-before-delivery school ordering for the
      // NEXT weekday is missed when that falls in a later order week (a Friday
      // packet for Monday) and none of its schools has ordered. Counting the
      // next weekday too would watermark every Friday packet for same-day schools
      // that can no longer order. Upgrade: have the server send each served
      // school's delivery days per menu.
      const key = `${dayKey}|${school.schoolLocationId}`
      if (!ordered.has(key) && !noService.has(key)) {
        windows.push({
          dayKey,
          schoolLocationId: school.schoolLocationId,
          closesAt: orderingCutoffFor(dayKey, school.deadline),
        })
      }
    }
  }
  return windows
}

/**
 * When the printed days' counts become final: the LAST ordering window on
 * them, whether it has passed or not. Null when nothing on the days can be
 * ordered at all (no shipments and no served schools).
 */
export function ordersFinalAt(input: OrdersFinalInput): Date | null {
  let latest: Date | null = null
  for (const { closesAt } of printWindows(input)) {
    if (latest === null || closesAt.getTime() > latest.getTime()) {
      latest = closesAt
    }
  }
  return latest
}

/** When the printed days' counts become final, or null when they already are. */
export function printLocksAt({ now, ...input }: PrintLockInput): Date | null {
  const finalAt = ordersFinalAt(input)
  return finalAt !== null && finalAt.getTime() > now.getTime() ? finalAt : null
}

/** The windows on the printed days that are still open at `now`, in no particular order. */
export function openPrintWindows({ now, ...input }: PrintLockInput): PrintWindow[] {
  return printWindows(input).filter((w) => w.closesAt.getTime() > now.getTime())
}
